import { format } from 'date-fns';
import { isDueUrgent } from '../../../../../../components/gamification/dueUrgency';
import type { ProgramDueItem } from '../../../../../../components/gamification/gamificationTypes';
import type { ProgramCardData, ProgramDueDate, ProgramDueDateRule } from './types';

// Program due dates on the admin Learning Framework. The rules and how a
// learner's own date resolves live on the BE (groupsService.resolveDueBy); the
// admin side works from the one date every existing learner shares, since new
// starters' dates are per person.

const toRule = (raw: any): ProgramDueDateRule | null => {
  if (!raw?.type) return null;
  return {
    type: raw.type,
    value: typeof raw.value === 'number' ? raw.value : null,
    resolvedAt: typeof raw.resolvedAt === 'number' ? raw.resolvedAt : null
  };
};

/**
 * The BE's stored programDueDate in the card's shape. Programs set before the
 * existing / new split carry one flat rule, which applied to everyone.
 */
export const normalizeProgramDueDate = (raw: any): ProgramDueDate | null => {
  if (!raw) return null;
  if (raw.existingMembers || raw.newMembers) {
    return { existingMembers: toRule(raw.existingMembers), newMembers: toRule(raw.newMembers) };
  }
  const flat = toRule(raw);
  return flat ? { existingMembers: flat, newMembers: flat } : null;
};

/**
 * The one date every existing learner shares, in unix seconds. Null when there
 * is no date. A legacy flat days-after-signup rule has no shared date: it
 * resolves per learner.
 */
export const existingLearnersDueBy = (pdd: ProgramDueDate | null): number | null => {
  const rule = pdd?.existingMembers;
  if (!rule) return null;
  if (rule.type === 'calendarDate') return rule.value || null;
  if (rule.type === 'daysAfterSignup') return rule.resolvedAt || null;
  return null;
};

/**
 * When this program's learners owe it, for the admin's Programs Due: a live
 * program with a date for its existing learners that not everyone has
 * completed. Null otherwise.
 */
const programDueBy = (program: ProgramCardData): number | null => {
  if (program.status !== 'invited' && program.status !== 'active') return null;
  if (program.programCompletionRate >= 100) return null;
  return existingLearnersDueBy(program.programDueDate);
};

/**
 * The admin's Programs Due: the programs they run that learners owe, soonest
 * first. The admin counterpart of the learner's list, which is their own
 * enrolments.
 */
export const adminProgramsDue = (programs: ProgramCardData[], nowSeconds: number): ProgramDueItem[] =>
  programs
    .map((program) => ({ program, dueBy: programDueBy(program) }))
    .filter((row): row is { program: ProgramCardData; dueBy: number } => row.dueBy !== null)
    .sort((a, b) => a.dueBy - b.dueBy)
    .map(({ program, dueBy }) => ({
      programName: program.name,
      due: format(new Date(dueBy * 1000), 'd MMM yyyy'),
      dueBy,
      isOverdue: dueBy < nowSeconds
    }));

/** In the Urgent Zone of the admin's Programs Due — the card's "!" and red outline. */
export const isProgramDueUrgent = (program: ProgramCardData, nowSeconds: number): boolean => {
  const due = programDueBy(program);
  return due !== null && isDueUrgent(due, nowSeconds);
};
